// ./src/hooks/useEntranceAnimation.ts
//
// Reusable hook for staggered entrance animations on list components.
//
// Tracks whether the initial entrance animation has played so that
// subsequent re-renders (filter changes, new items) skip the stagger.
// Once the animation window expires, Framer Motion variants are no
// longer applied — items appear instantly.
//
// Usage:
//   const { hasAnimated, listVariants, cardVariants } = useEntranceAnimation({
//     itemCount: records.length,
//   });
//
//   <motion.div variants={hasAnimated ? undefined : listVariants} ...>
//     {items.map(item => (
//       <motion.div key={item.id} variants={hasAnimated ? undefined : cardVariants} ...>

import { useState, useEffect, useRef, useMemo } from "react";

// ============================================
// Types
// ============================================

export interface UseEntranceAnimationOptions {
  /** Number of items currently in the list. Animation fires once this is > 0. */
  itemCount: number;

  /**
   * Per-item stagger delay in ms (default: 50).
   * Total animation window = itemCount * staggerMs + baseDelayMs, capped at maxDelayMs.
   */
  staggerMs?: number;

  /** Base delay added after the stagger total (default: 300). */
  baseDelayMs?: number;

  /** Maximum total delay before marking animation as complete (default: 2000). */
  maxDelayMs?: number;

  /** Set to false to disable the animation entirely (items appear instantly). */
  enabled?: boolean;
}

export interface UseEntranceAnimationReturn {
  /** Whether the entrance animation has already played. */
  hasAnimated: boolean;
}

// ============================================
// Default animation variants (importable)
// ============================================

/** Stagger container — apply to the parent wrapper. */
export const entranceListVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.05 },
  },
};

/** Fade-up card — apply to each child. */
export const entranceCardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.2 },
  },
};

// ============================================
// Hook
// ============================================

export const useEntranceAnimation = (
  options: UseEntranceAnimationOptions
): UseEntranceAnimationReturn => {
  const {
    itemCount,
    staggerMs = 50,
    baseDelayMs = 300,
    maxDelayMs = 2000,
    enabled = true,
  } = options;

  const hasAnimatedRef = useRef(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    // Already played or disabled — nothing to do
    if (hasAnimatedRef.current || !enabled) return;

    // No items yet — wait for data to arrive
    if (itemCount === 0) return;

    const delay = Math.min(itemCount * staggerMs + baseDelayMs, maxDelayMs);

    const timer = setTimeout(() => {
      hasAnimatedRef.current = true;
      setHasAnimated(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [itemCount, staggerMs, baseDelayMs, maxDelayMs, enabled]);

  // If disabled, report as already animated so variants are never applied
  return useMemo(
    () => ({ hasAnimated: hasAnimated || !enabled }),
    [hasAnimated, enabled]
  );
};